import { useEffect, useState, useMemo } from 'react';
import {
  isString, isNil, isEmpty, throttle, isNumber, find,
} from 'lodash';
import { useSnackbar } from 'notistack';
import Autocomplete from '@mui/material/Autocomplete';
import { TextField } from '@mui/material';
import { useApi } from 'lib/contexts/ApplicationState';
import { TagValue } from 'lib/types/tags';
import { getTagTypeName } from 'lib/constants';
import { TagValueIcon } from './TagValueIcon';

const FORBIDDEN_TAG_CHARACTERS = /[,#@]/;
const ADD_PREFIX = 'Add "';

interface TagValueOption extends TagValue {
  inputValue?: string;
}

interface Props {
  label: string;
  tagTypeId: number;
  onSelectTag: (tagValue: TagValueOption) => void;
  postId?: number;
  /** skipSaveOnSelect will skip tag creation when onSelectTag
 * is handling creation/saving instead (ie for Concepts) */
  skipSaveOnSelect?: boolean;
}

export const SingleCreatableTagValueSearch = ({
  label, tagTypeId, onSelectTag, postId, skipSaveOnSelect = false,
}: Props) => {
  const [inputValue, setInputValue] = useState('');
  const [errorMessage, setErrorMessage] = useState('');
  const [options, setOptions] = useState<TagValueOption[]>([]);
  const API = useApi();
  const { enqueueSnackbar } = useSnackbar();

  const fetchTags = useMemo(
    () => throttle(async (request, callback) => {
      const results = await API.tagValuesSearch(request.inputValue, request.tagTypeId);
      callback(results);
    }, 300),
    [],
  );

  const fetchSuggestedTagsForPost = async () => {
    if (postId) {
      const suggestedTags = await API.suggestTagsForPostId(tagTypeId, postId);
      setOptions(suggestedTags);
    }
  };

  useEffect(() => {
    if (postId && !inputValue) {
      fetchSuggestedTagsForPost();
    }
  }, [postId, inputValue]);

  const cleanTag = (tag: string) => {
    const clean = tag.trim().toLocaleLowerCase();
    if (!clean.startsWith(ADD_PREFIX.toLocaleLowerCase())) {
      return clean;
    }

    const lastQuote = clean.lastIndexOf('"');
    if (lastQuote > -1) {
      return clean.substring(ADD_PREFIX.length, lastQuote);
    }
    return clean.substring(ADD_PREFIX.length);
  };

  // This is triggered when a user selects the "Add..." option
  const addNewTag = async (tag: string) => {
    if (tag && skipSaveOnSelect) {
      return onSelectTag({ value: tag } as TagValueOption);
    }
    if (tag) {
      try {
        return API.createNewTagValue(tagTypeId, tag);
      } catch (error: any) {
        enqueueSnackbar(`There was an error: ${error.message}`, { variant: 'error' });
      }
    }
    return null;
  };

  // This is triggered when a user types something and hits enter without selecting an option
  const handleNoSelectedSuggestion = async (givenInput: string) => {
    // If the user types something that exactly matches what is in the options array,
    // just return that tag value. This avoids trying to create a duplicate tag value.
    const exactMatch = find(options,
      (tagOption) => tagOption.value.toLocaleLowerCase() === givenInput.toLocaleLowerCase());
    if (exactMatch) {
      return exactMatch;
    }
    // Otherwise, create a new tag using that text
    if (givenInput !== '') {
      return addNewTag(cleanTag(givenInput));
    }
    return null;
  };

  const isValidInput = (input: string) => !FORBIDDEN_TAG_CHARACTERS.test(input);

  const onChangeTagValue = async (value: TagValueOption | string | null) => {
    if (isNil(value) || isEmpty(value)) return;

    if (isString(value)) {
      if (!isValidInput(value)) {
        setErrorMessage('');
        return;
      }
      const tag = await handleNoSelectedSuggestion(value);
      if (tag) onSelectTag(tag);
    } else {
      /* if user select "Add xxx" (to create a new tag) */
      if (value?.inputValue) {
        try {
          const newTag = await addNewTag(value.inputValue);
          if (newTag) onSelectTag(newTag);
          return;
        } catch (error: any) {
          enqueueSnackbar(`There was an error: ${error.message}`, { variant: 'error' });
        }
      }

      onSelectTag(value);
    }
  };

  useEffect(() => {
    let active = true;
    if (isEmpty(inputValue)) {
      setOptions([]);
      setErrorMessage('');
      return () => {
        active = true;
      };
    }
    if (isValidInput(inputValue)) {
      const query = {
        inputValue,
        tagTypeId: isNumber(tagTypeId) ? tagTypeId : undefined,
      };

      // When adding a tag, we sometimes try to autocomplete the Add... option
      if (query && !query.inputValue.startsWith(ADD_PREFIX)) {
        setErrorMessage('');
        fetchTags(query, (results: any) => {
          if (active) {
            const newOptions = results || [];
            setOptions(newOptions);
          }
        });
      }
    } else {
      const lastTypedLetter = inputValue[inputValue.length - 1];
      if (!isValidInput(lastTypedLetter)) {
        setErrorMessage(`"${lastTypedLetter}" is not an allowed character`);
      }
    }
    return () => {
      active = false;
    };
  }, [inputValue, fetchTags]);

  return (
    <>
      <p className="text-sm text-orange w-full">{errorMessage}</p>
      <Autocomplete
        data-testid="autocomplete"
        freeSolo
        options={options}
        getOptionLabel={(option) => option.value || ''}
        filterOptions={(fOptions) => {
          const exactMatch = find(
            fOptions, (tagOption) => tagOption.value.toLowerCase() === inputValue.toLowerCase(),
          );
          if (inputValue !== '' && isValidInput(inputValue) && (fOptions.length <= 0 || !exactMatch)) {
            fOptions.push({
              id: -1,
              value: `${ADD_PREFIX}${inputValue}"`,
              inputValue,
              tagTypeId,
            });
          }
          return fOptions;
        }}
        selectOnFocus
        clearOnBlur
        handleHomeEndKeys
        autoComplete
        includeInputInList
        filterSelectedOptions
        onChange={(_, value) => onChangeTagValue(value)}
        onInputChange={(_, newInputValue) => setInputValue(newInputValue)}
        renderInput={(params) => (
          <TextField autoFocus {...params} label={label || 'Tags'} fullWidth size="small" />
        )}
        renderOption={(props, option: TagValueOption) => (
          <li key={option.id} {...props}>
            <div className="flex">
              <div className="mr-2">
                <TagValueIcon tagTypeId={tagTypeId || option.tagTypeId} />
              </div>
              <div>
                <div className="font-bold">
                  {option.value}
                </div>
                <div>
                  {getTagTypeName(option.tagTypeId)}
                </div>
              </div>
            </div>
          </li>
        )}
      />
    </>
  );
};
